import React, {useEffect, useState} from 'react';
import {Badge, Button, Card, Input, Modal, Select, Space, Spin, Switch, Table, Tooltip, Typography, message, Popconfirm} from 'antd';
import {InfoCircleOutlined, ReloadOutlined, SearchOutlined, RedoOutlined, DeleteOutlined} from '@ant-design/icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpotify, faYoutube} from '@fortawesome/free-brands-svg-icons';
import {faB} from '@fortawesome/free-solid-svg-icons';
import axiosApiInstance from '../../../api/axiosClient';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {PageContainer} from "@ant-design/pro-components";

dayjs.extend(relativeTime);

const {Title, Text} = Typography;
const {Option} = Select;

interface WorkflowStatus {
    workflow_id: string;
    run_id: string;
    platform: string;
    action: string;
    release_key: string;
    status: string;
    start_time: string;
    close_time?: string;
    last_error?: string;
}

interface WorkflowStatusResponse {
    workflows: WorkflowStatus[];
}

const WorkflowStatus: React.FC = () => {
    const [workflowData, setWorkflowData] = useState<WorkflowStatus[]>([]);
    const [filteredData, setFilteredData] = useState<WorkflowStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowStatus | null>(null);
    const [refreshTimer, setRefreshTimer] = useState<number | null>(null);
    const [releaseSearchText, setReleaseSearchText] = useState('');
    const [platformFilter, setPlatformFilter] = useState<string>('');
    const [actionFilter, setActionFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [restartLoading, setRestartLoading] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

    const fetchWorkflows = async () => {
        setLoading(true);
        try {
            const response = await axiosApiInstance.get<WorkflowStatusResponse>('/api/products/workflows/');
            setWorkflowData(response.data.workflows);
            setFilteredData(response.data.workflows);
        } catch (error) {
            console.error('Error fetching workflow data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWorkflows();

        // Set up auto-refresh timer if enabled
        if (autoRefresh) {
            const timer = window.setInterval(() => {
                fetchWorkflows();
            }, 5000);

            setRefreshTimer(timer);
        }

        return () => {
            if (refreshTimer) {
                clearInterval(refreshTimer);
            }
        };
    }, [autoRefresh]);

    useEffect(() => {
        applyFilters();
    }, [releaseSearchText, platformFilter, actionFilter, statusFilter, workflowData]);

    const applyFilters = () => {
        let filtered = [...workflowData];

        if (releaseSearchText) {
            // Split by comma and trim whitespace
            const searchTerms = releaseSearchText.split(',').map(term => term.trim()).filter(Boolean);

            if (searchTerms.length > 0) {
                filtered = filtered.filter(workflow => 
                    searchTerms.some(term => workflow.release_key.toLowerCase().includes(term.toLowerCase()))
                );
            }
        }

        if (platformFilter) {
            filtered = filtered.filter(workflow => workflow.platform === platformFilter);
        }

        if (actionFilter) {
            filtered = filtered.filter(workflow => (workflow.action || 'upload') === actionFilter);
        }

        if (statusFilter) {
            filtered = filtered.filter(workflow => workflow.status === statusFilter);
        }

        setFilteredData(filtered);
    };

    const getPlatformIcon = (platform: string) => {
        switch (platform) {
            case 'youtube':
                return <FontAwesomeIcon icon={faYoutube}/>;
            case 'believe':
                return <FontAwesomeIcon icon={faB}/>;
            case 'spotify':
                return <FontAwesomeIcon icon={faSpotify}/>;
            default:
                return null;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'RUNNING':
                return <Badge status="processing" text="Running"/>;
            case 'COMPLETED':
                return <Badge status="success" text="Completed"/>;
            case 'FAILED':
                return <Badge status="error" text="Failed"/>;
            case 'CANCELED':
                return <Badge status="default" text="Canceled"/>;
            case 'TERMINATED':
                return <Badge status="warning" text="Terminated"/>;
            case 'TIMED_OUT':
                return <Badge status="warning" text="Timed Out"/>;
            default:
                return <Badge status="default" text={status}/>;
        }
    };

    const showWorkflowDetails = (workflow: WorkflowStatus) => {
        setSelectedWorkflow(workflow);
        setModalVisible(true);
    };

    const handleReleaseSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReleaseSearchText(e.target.value);
    };

    const handlePlatformFilterChange = (value: string) => {
        setPlatformFilter(value);
    };

    const handleStatusFilterChange = (value: string) => {
        setStatusFilter(value);
    };

    const resetFilters = () => {
        setReleaseSearchText('');
        setPlatformFilter('');
        setActionFilter('');
        setStatusFilter('');
    };

    const getActionBadge = (action: string) => {
        if (action === 'takedown') {
            return <Badge status="error" text="Takedown"/>;
        }
        return <Badge status="success" text="Upload"/>;
    };

    const handleAutoRefreshChange = (checked: boolean) => {
        setAutoRefresh(checked);

        // If turning off auto-refresh, clear any existing timer
        if (!checked && refreshTimer) {
            clearInterval(refreshTimer);
            setRefreshTimer(null);
        }
    };

    const restartWorkflow = async (workflow: WorkflowStatus) => {
        try {
            setRestartLoading(workflow.workflow_id);

            // Call the API to restart the workflow
            const response = await axiosApiInstance.put(`/api/products/workflows/${workflow.workflow_id}/restart`);

            if (response.status === 200) {
                message.success(`Workflow for ${workflow.release_key} restarted successfully`);

                // Refresh the workflows list
                fetchWorkflows();
            }
        } catch (error) {
            console.error('Error restarting workflow:', error);
            message.error(`Failed to restart workflow: ${error.response?.data?.error || 'Unknown error'}`);
        } finally {
            setRestartLoading(null);
        }
    };

    const deleteWorkflow = async (workflow: WorkflowStatus) => {
        try {
            setDeleteLoading(workflow.workflow_id);

            // Call the API to delete the workflow with run_id as a query parameter
            const response = await axiosApiInstance.delete(`/api/products/workflows/${workflow.workflow_id}?run_id=${workflow.run_id}`);

            if (response.status === 200) {
                message.success(`Workflow for ${workflow.release_key} deleted successfully`);

                // Close the modal if it's open
                if (modalVisible && selectedWorkflow?.workflow_id === workflow.workflow_id) {
                    setModalVisible(false);
                }

                // Refresh the workflows list
                fetchWorkflows();
            }
        } catch (error) {
            console.error('Error deleting workflow:', error);
            message.error(`Failed to delete workflow: ${error.response?.data?.error || 'Unknown error'}`);
        } finally {
            setDeleteLoading(null);
        }
    };

    const getPlatformOptions = () => {
        const platforms = Array.from(new Set(workflowData.map(workflow => workflow.platform)));
        return platforms.map(platform => (
            <Option key={platform} value={platform}>
                <Space>
                    {getPlatformIcon(platform)}
                    <span style={{textTransform: 'capitalize'}}>{platform}</span>
                </Space>
            </Option>
        ));
    };

    const getStatusOptions = () => {
        const statuses = Array.from(new Set(workflowData.map(workflow => workflow.status)));
        return statuses.map(status => (
            <Option key={status} value={status}>
                {getStatusBadge(status)}
            </Option>
        ));
    };

    const columns = [
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            render: (text: string) => (
                <Space>
                    {getPlatformIcon(text)}
                    <span style={{textTransform: 'capitalize'}}>{text}</span>
                </Space>
            ),
        },
        {
            title: 'UPC',
            dataIndex: 'release_key',
            key: 'release_key',
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (action: string) => getActionBadge(action),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => getStatusBadge(status),
        },
        {
            title: 'Started',
            dataIndex: 'start_time',
            key: 'start_time',
            render: (time: string) => (
                <Tooltip title={dayjs(time).format('YYYY-MM-DD HH:mm:ss')}>
                    {dayjs(time).fromNow()}
                </Tooltip>
            ),
        },
        {
            title: 'Duration',
            key: 'duration',
            render: (_, record: WorkflowStatus) => {
                if (record.status == "RUNNING") return '-';
                const startTime = dayjs(record.start_time);
                const endTime = record.close_time ? dayjs(record.close_time) : dayjs();
                const duration = endTime.diff(startTime, 'second');

                const minutes = Math.floor(duration / 60);
                const seconds = duration % 60;

                return `${minutes}m ${seconds}s`;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: WorkflowStatus) => {
                // Check if workflow is in a terminal state and can be restarted
                const canRestart = ['FAILED', 'COMPLETED', 'CANCELED', 'TERMINATED', 'TIMED_OUT'].includes(record.status);

                return (
                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            icon={<InfoCircleOutlined/>}
                            onClick={() => showWorkflowDetails(record)}
                        >
                            Details
                        </Button>

                        {canRestart && (
                            <Popconfirm
                                title="Are you sure?"
                                onConfirm={() => restartWorkflow(record)}
                                okText="Yes"
                                cancelText="No"
                                disabled={restartLoading !== null || deleteLoading !== null}
                            >
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<RedoOutlined />}
                                    loading={restartLoading === record.workflow_id}
                                    disabled={restartLoading !== null || deleteLoading !== null}
                                >
                                    Retry
                                </Button>
                            </Popconfirm>
                        )}

                        <Popconfirm
                            // title="Delete Workflow"
                            title="Are you sure?"
                            onConfirm={() => deleteWorkflow(record)}
                            okText="Yes"
                            cancelText="No"
                            disabled={deleteLoading !== null || restartLoading !== null}
                        >
                            <Button
                                type="default"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                loading={deleteLoading === record.workflow_id}
                                disabled={deleteLoading !== null || restartLoading !== null}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <PageContainer>

            <Card>
                <Space direction="vertical" size="large" style={{width: '100%'}}>
                    <Space style={{justifyContent: 'space-between', width: '100%'}}>
                        <Space style={{width: '100%'}} wrap>
                            <Input
                                placeholder="Search by UPC (comma separated)"
                                value={releaseSearchText}
                                onChange={handleReleaseSearch}
                                prefix={<SearchOutlined/>}
                                style={{width: 250}}
                                allowClear
                            />
                            <Select
                                placeholder="Filter by Platform"
                                value={platformFilter}
                                onChange={handlePlatformFilterChange}
                                style={{width: 200}}
                                allowClear
                            >
                                {getPlatformOptions()}
                            </Select>
                            <Select
                                placeholder="Filter by Action"
                                value={actionFilter}
                                onChange={setActionFilter}
                                style={{width: 180}}
                                allowClear
                            >
                                <Option value="upload">{getActionBadge('upload')}</Option>
                                <Option value="takedown">{getActionBadge('takedown')}</Option>
                            </Select>
                            <Select
                                placeholder="Filter by Status"
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                style={{width: 200}}
                                allowClear
                            >
                                {getStatusOptions()}
                            </Select>
                            {(releaseSearchText || platformFilter || actionFilter || statusFilter) && (
                                <Button onClick={resetFilters}>Clear Filters</Button>
                            )}
                        </Space>

                        <Space>
                          <span>
                            <Text>Auto-refresh: </Text>
                            <Switch
                                checked={autoRefresh}
                                onChange={handleAutoRefreshChange}
                                size="small"
                            />
                          </span>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined/>}
                                onClick={fetchWorkflows}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Space>



                    {loading ? (
                        <div style={{textAlign: 'center', padding: '20px'}}>
                            <Spin size="large"/>
                        </div>
                    ) : (
                        <Table
                            dataSource={filteredData}
                            columns={columns}
                            rowKey="workflow_id"
                            pagination={{pageSize: 10}}
                        />
                    )}
                </Space>
            </Card>

            <Modal
                title={
                    <Space>
                        {selectedWorkflow && getPlatformIcon(selectedWorkflow.platform)}
                        <span>Workflow Details</span>
                    </Space>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={[
                    selectedWorkflow && ['FAILED', 'COMPLETED', 'CANCELED', 'TERMINATED', 'TIMED_OUT'].includes(selectedWorkflow.status) && (
                        <Popconfirm
                            key="retry"
                            title="Are you sure?"
                            onConfirm={() => {
                                setModalVisible(false);
                                restartWorkflow(selectedWorkflow);
                            }}
                            okText="Yes"
                            cancelText="No"
                            disabled={restartLoading !== null || deleteLoading !== null}
                        >
                            <Button 
                                type="primary"
                                icon={<RedoOutlined />}
                                loading={restartLoading === selectedWorkflow.workflow_id}
                                disabled={restartLoading !== null || deleteLoading !== null}
                            >
                                New Run
                            </Button>
                        </Popconfirm>
                    ),
                    selectedWorkflow && (
                        <Popconfirm
                            key="delete"
                            // title="Delete Workflow"
                            title="Are you sure?"
                            onConfirm={() => deleteWorkflow(selectedWorkflow)}
                            okText="Yes"
                            cancelText="No"
                            disabled={deleteLoading !== null || restartLoading !== null}
                        >
                            <Button 
                                danger
                                icon={<DeleteOutlined />}
                                loading={deleteLoading === selectedWorkflow.workflow_id}
                                disabled={deleteLoading !== null || restartLoading !== null}
                            >
                                Delete Workflow
                            </Button>
                        </Popconfirm>
                    ),
                    <Button key="close" onClick={() => setModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
            >
                {selectedWorkflow && (
                    <div>
                        <Card>
                            <Space direction="vertical" size="small">
                                <Space>
                                    <Text strong>Platform:</Text>
                                    <Text>{selectedWorkflow.platform}</Text>
                                </Space>
                                <Space>
                                    <Text strong>Action:</Text>
                                    <Text>{getActionBadge(selectedWorkflow.action)}</Text>
                                </Space>
                                <Space>
                                    <Text strong>Status:</Text>
                                    <Text>{getStatusBadge(selectedWorkflow.status)}</Text>
                                </Space>
                                <Space>
                                    <Text strong>UPC:</Text>
                                    <Text>{selectedWorkflow.release_key}</Text>
                                </Space>
                                <Space>
                                    <Text strong>Started:</Text>
                                    <Text>{dayjs(selectedWorkflow.start_time).format('YYYY-MM-DD HH:mm:ss')}</Text>
                                </Space>
                                {selectedWorkflow.close_time && (
                                    <Space>
                                        <Text strong>Completed:</Text>
                                        <Text>{dayjs(selectedWorkflow.close_time).format('YYYY-MM-DD HH:mm:ss')}</Text>
                                    </Space>
                                )}
                                <Space>
                                    <Text strong>Workflow ID:</Text>
                                    <Text copyable>{selectedWorkflow.workflow_id}</Text>
                                </Space>
                                <Space>
                                    <Text strong>Run ID:</Text>
                                    <Text copyable>{selectedWorkflow.run_id}</Text>
                                </Space>
                                {selectedWorkflow.last_error && (
                                    <div>
                                        <Text strong>Error:</Text>
                                        <div
                                            style={{
                                                background: '#fff1f0',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                marginTop: '8px',
                                                border: '1px solid #ffccc7'
                                            }}
                                        >
                                            <Text type="danger">{selectedWorkflow.last_error}</Text>
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Card>
                    </div>
                )}
            </Modal>

        </PageContainer>
    );
};

export default WorkflowStatus;
